import axios from 'axios';
import type { Dispatch } from '@reduxjs/toolkit';

import { actions } from 'features/auth/slice';
import { findCountryWithAgencyConfigForUser } from 'features/auth/helpers';
import type { CountryCodeEnum } from 'features/common/types';
import { getPassengerDetails } from '../services';
import type { IPassengerDetail } from '../types';

/**
 * Contexto de agencia para la página de cliente/pasajero — mismo patrón que
 * la orden de venta: el link puede traer ?agencyId=N, y si no (o si es otro),
 * se prueba el fetch contra las agencies del usuario hasta encontrarlo y se
 * cambia el ambiente automáticamente. Sin esto, abrir un cliente de Colombia
 * con el ambiente en Uruguay daba 404.
 */

export function parseAgencyIdFromSearch(search: string): number | undefined {
	const raw = new URLSearchParams(search).get('agencyId');
	if (!raw) return undefined;
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function buildAgencyTryOrder(userAgencyIds: number[], preferredAgencyId?: number): number[] {
	const unique = Array.from(new Set(userAgencyIds));
	if (preferredAgencyId && unique.includes(preferredAgencyId)) {
		return [preferredAgencyId, ...unique.filter((id) => id !== preferredAgencyId)];
	}
	return unique;
}

async function fetchPassengerAcrossAgencies(
	passengerId: string,
	userAgencyIds: number[],
	preferredAgencyId?: number,
): Promise<{ data: IPassengerDetail; resolvedAgencyId: number }> {
	const agenciesToTry = buildAgencyTryOrder(userAgencyIds, preferredAgencyId);
	let lastError: unknown;

	for (const agencyId of agenciesToTry) {
		try {
			const { data } = await getPassengerDetails(passengerId, { agencyId });
			return { data, resolvedAgencyId: agencyId };
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				lastError = error;
				continue;
			}
			throw error;
		}
	}

	throw lastError ?? new Error('No se encontró el cliente');
}

export async function loadPassengerWithAgencyContext({
	passengerId,
	preferredAgencyId,
	userAgencyIds,
	currentCountry,
	dispatch,
}: {
	passengerId: string;
	preferredAgencyId?: number;
	userAgencyIds: number[];
	currentCountry: CountryCodeEnum;
	dispatch: Dispatch;
}): Promise<{
	data: IPassengerDetail;
	resolvedAgencyId: number;
	switched: boolean;
	countryName: string;
}> {
	if (userAgencyIds.length === 0) {
		throw new Error('No tenés agencias asignadas para consultar este cliente.');
	}

	const { data, resolvedAgencyId } = await fetchPassengerAcrossAgencies(passengerId, userAgencyIds, preferredAgencyId);

	const config = findCountryWithAgencyConfigForUser(resolvedAgencyId);
	if (!config) {
		throw new Error('No tenés permiso para acceder a la agencia de este cliente.');
	}

	const switched = config.id !== currentCountry;
	if (switched) {
		dispatch(actions.setUpdateCountry(config.id));
	}

	return { data, resolvedAgencyId, switched, countryName: config.countryName };
}
